import { supabase } from '../lib/supabase';

// Fetch analytics data for user's posts
export async function fetchAnalyticsData(forceRefresh = false) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    
    if (sessionError || !session?.access_token) {
      throw new Error('Session not found');
    }

    // Call Edge Function to fetch analytics
    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/analytics-fetcher`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          forceRefresh
        })
      }
    );

    const result = await response?.json();

    if (!result?.success) {
      throw new Error(result?.error || 'Failed to fetch analytics');
    }

    return result;
  } catch (error) {
    console.error('Analytics fetch error:', error);
    throw error;
  }
}

// Get aggregated metrics for dashboard
export async function getDashboardMetrics() {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Get all content posts with analytics data
    const { data: posts, error: postsError } = await supabase
      .from('content_posts')
      .select(`
        id,
        linkedin_impressions,
        linkedin_engagement_count,
        linkedin_reach,
        twitter_impressions,
        twitter_engagement_count,
        twitter_reach,
        created_at,
        linkedin_content,
        twitter_content
      `)
      .eq('user_id', user?.id)
      .order('created_at', { ascending: false });

    if (postsError) {
      throw new Error(`Failed to fetch posts: ${postsError.message}`);
    }

    // Calculate aggregated metrics
    const currentDate = new Date();
    const lastWeek = new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    const lastMonth = new Date(currentDate.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Filter posts by time periods
    const allPosts = posts || [];
    const lastWeekPosts = allPosts.filter(post => new Date(post.created_at) >= lastWeek);
    const lastMonthPosts = allPosts.filter(post => new Date(post.created_at) >= lastMonth);

    // Calculate total impressions
    const totalImpressions = allPosts.reduce((sum, post) => {
      return sum + (post.linkedin_impressions || 0) + (post.twitter_impressions || 0);
    }, 0);

    const lastWeekImpressions = lastWeekPosts.reduce((sum, post) => {
      return sum + (post.linkedin_impressions || 0) + (post.twitter_impressions || 0);
    }, 0);

    // Calculate total engagement
    const totalEngagement = allPosts.reduce((sum, post) => {
      return sum + (post.linkedin_engagement_count || 0) + (post.twitter_engagement_count || 0);
    }, 0);

    const lastWeekEngagement = lastWeekPosts.reduce((sum, post) => {
      return sum + (post.linkedin_engagement_count || 0) + (post.twitter_engagement_count || 0);
    }, 0);

    // Calculate engagement rate
    const engagementRate = totalImpressions > 0 ? (totalEngagement / totalImpressions * 100) : 0;
    const lastWeekEngagementRate = lastWeekImpressions > 0 ? (lastWeekEngagement / lastWeekImpressions * 100) : 0;

    // Calculate total posts this month
    const totalPostsThisMonth = lastMonthPosts.reduce((count, post) => {
      const hasContent = (post.linkedin_content && post.linkedin_content.trim()) || 
                        (post.twitter_content && post.twitter_content.trim());
      return hasContent ? count + 1 : count;
    }, 0);

    // Calculate average reach
    const totalReach = allPosts.reduce((sum, post) => {
      return sum + (post.linkedin_reach || 0) + (post.twitter_reach || 0);
    }, 0);

    const lastWeekReach = lastWeekPosts.reduce((sum, post) => {
      return sum + (post.linkedin_reach || 0) + (post.twitter_reach || 0);
    }, 0);

    const avgReach = allPosts.length > 0 ? Math.round(totalReach / allPosts.length) : 0;
    const lastWeekAvgReach = lastWeekPosts.length > 0 ? Math.round(lastWeekReach / lastWeekPosts.length) : 0;

    // Calculate percentage changes
    const impressionsChange = calculatePercentageChange(totalImpressions - lastWeekImpressions, lastWeekImpressions);
    const engagementChange = calculatePercentageChange(engagementRate, lastWeekEngagementRate);
    const reachChange = calculatePercentageChange(avgReach, lastWeekAvgReach);

    return {
      totalImpressions: {
        value: formatNumber(totalImpressions),
        change: impressionsChange.formatted,
        changeType: impressionsChange.type
      },
      engagementRate: {
        value: `${engagementRate.toFixed(1)}%`,
        change: engagementChange.formatted,
        changeType: engagementChange.type
      },
      totalPosts: {
        value: totalPostsThisMonth.toString(),
        change: `+${totalPostsThisMonth}`,
        changeType: totalPostsThisMonth > 0 ? 'positive' : 'neutral'
      },
      avgReach: {
        value: formatNumber(avgReach),
        change: reachChange.formatted,
        changeType: reachChange.type
      }
    };
  } catch (error) {
    console.error('Dashboard metrics error:', error);
    throw error;
  }
}

// Helper function to calculate percentage change
function calculatePercentageChange(current, previous) {
  if (previous === 0) {
    return {
      formatted: current > 0 ? '+∞%' : '+0%',
      type: current > 0 ? 'positive' : 'neutral'
    };
  }

  const change = ((current - previous) / previous) * 100;
  const formatted = change >= 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
  const type = change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral';

  return { formatted, type };
}

// Helper function to format numbers
function formatNumber(num) {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return num.toString();
}